import { useMemo, useState, useCallback } from 'react'
import { useParams, Link, useNavigate, useSearch } from '@tanstack/react-router'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Input } from './ui/input'
import { AppIcon } from './ui/icon'
import { useComplianceControls, COMPLIANCE_DOMAINS, DOMAIN_LABELS } from '../hooks/useComplianceApi'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useLocale } from '../contexts/LocaleContext'
import { useTranslation } from 'react-i18next'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { mdiClipboardCheckOutline, mdiCog, mdiLoading, mdiPlus } from '@mdi/js'

export function ComplianceControlsPage() {
  const { org } = useParams({ strict: false })
  const { locale } = useLocale()
  const { t } = useTranslation('compliance')
  const navigate = useNavigate()

  const getLocalizedName = useCallback((name: Record<string, string> | undefined): string => {
    if (!name) return 'Unnamed'
    return name[locale] || name.en || name.fr || Object.values(name)[0] || 'Unnamed'
  }, [locale])
  const search = useSearch({ strict: false }) as { domain_code?: string } | undefined
  const domainFilter = search?.domain_code || 'all'
  const [searchQuery, setSearchQuery] = useState('')

  const { data: controls = [], isLoading, error } = useComplianceControls(org || 'demo')

  const breadcrumbTitle = useMemo(() => {
    if (domainFilter && domainFilter !== 'all') {
      const label = DOMAIN_LABELS[domainFilter] || domainFilter.replace(/_/g, ' ')
      return `${t('breadcrumb.compliance')} (${label})`
    }
    return t('breadcrumb.compliance')
  }, [domainFilter, t])

  useBreadcrumbs(useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${org}` },
    { title: t('breadcrumb.org_settings'), path: `/${org}/settings` },
    { title: breadcrumbTitle }
  ], [org, breadcrumbTitle, t]))

  const handleDomainFilterChange = (value: string) => {
    if (value === 'all') {
      navigate({
        to: '/$org/compliance-controls',
        params: { org: org || 'demo' },
        search: { domain_code: undefined },
      })
    } else {
      navigate({
        to: '/$org/compliance-controls',
        params: { org: org || 'demo' },
        search: { domain_code: value },
      })
    }
  }

  const filteredControls = useMemo(() => {
    let result = controls
    if (domainFilter && domainFilter !== 'all') {
      result = result.filter(c => c.domain === domainFilter)
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(c =>
        c.code.toLowerCase().includes(q) ||
        getLocalizedName(c.name).toLowerCase().includes(q)
      )
    }
    return result
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controls, domainFilter, searchQuery])

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.compliance}
            title={t('controls.title')}
            description={t('controls.subtitle', { count: controls.length })}
            actions={
              <>
          <Can perm="compliance.write">
            <Link to="/$org/compliance-controls/create" params={{ org: org || 'demo' }}>
              <Button data-testid="create-control-btn">
                <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                {t('controls.create')}
              </Button>
            </Link>
          </Can>
              </>
            }
          />
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-4">
            <Input
              placeholder={t('controls.search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="controls-search"
            />
            <Select value={domainFilter} onValueChange={handleDomainFilterChange}>
              <SelectTrigger className="w-[200px]" data-testid="domain-filter">
                <SelectValue placeholder={t('controls.filter_domain')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('controls.all_domains')}</SelectItem>
                {COMPLIANCE_DOMAINS.map(d => (
                  <SelectItem key={d} value={d}>{DOMAIN_LABELS[d] || d.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">{t('controls.error')}</div>
          ) : filteredControls.length === 0 ? (
            <div className="text-muted-foreground text-center py-8">
              {controls.length === 0 ? (
                <EmptyState
                  variant="inline"
                  tone="solid"
                  icon={mdiClipboardCheckOutline}
                  title={t('controls.empty_title')}
                  action={
                    <Can perm="compliance.write">
                      <Link to="/$org/compliance-controls/create" params={{ org: org || 'demo' }}>
                        <Button data-testid="create-first-control-btn">
                          <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                          {t('controls.create')}
                        </Button>
                      </Link>
                    </Can>
                  }
                />
              ) : (
                t('controls.no_match')
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('controls.column_code')}</TableHead>
                  <TableHead>{t('controls.column_name')}</TableHead>
                  <TableHead>{t('controls.column_domain')}</TableHead>
                  <TableHead>{t('controls.column_enabled')}</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredControls.map((control) => (
                  <TableRow
                    key={control.uid}
                    className="cursor-pointer"
                    onClick={() => navigate({
                      to: '/$org/compliance-rules',
                      params: { org: org || 'demo' },
                      search: { control_code: control.code },
                    })}
                    data-testid={`control-row-${control.uid}`}
                  >
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded">{control.code}</code>
                    </TableCell>
                    <TableCell className="font-medium">
                      {getLocalizedName(control.name)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{DOMAIN_LABELS[control.domain] || control.domain.replace(/_/g, ' ')}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={control.enabled ? 'default' : 'secondary'}>
                        {control.enabled ? t('controls.column_enabled') : t('common:status.disabled')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/$org/compliance-controls/$controlUid"
                        params={{ org: org || 'demo', controlUid: control.uid }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="ghost" size="sm" data-testid={`edit-control-${control.uid}`}>
                          <AppIcon path={mdiCog} size="sm" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}
